import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { type TestifyView, whenTestify } from '../../lib/testify'

// QUALITY CONTROL — the tester's workspace, as a page of Scout rather than an overlay: the board first
// (/quality), the stats behind it (/quality/stats), a ticket in the URL (?ticket=TFY-21) while its
// drawer is open. Scout draws the frame; the Testify widget draws everything inside `.quality-host`.
//
// The host must never depend on the guest: if /testify/testify.js is missing or the widget disabled
// itself, this page says so in one line and the rest of Scout is untouched.

export function Quality({ view }: { view: TestifyView }) {
  const host = useRef<HTMLDivElement>(null)
  const [params] = useSearchParams()
  // Read at render time, used only when the view (re)mounts: the widget updates ?ticket= itself as
  // drawers open and close, and re-rendering the board on each of those would reload it.
  const ticket = useRef<string | null>(null)
  ticket.current = params.get('ticket')
  const [state, setState] = useState<'loading' | 'ready' | 'absent'>('loading')

  useEffect(() => {
    let cancelled = false
    void whenTestify().then((api) => {
      if (cancelled) return
      const node = host.current
      if (!api || !node) {
        setState('absent')
        return
      }
      api.render(node, { view, compact: view === 'board', ticket: ticket.current ?? undefined })
      setState('ready')
    })
    return () => {
      cancelled = true
    }
  }, [view])

  return (
    <section className="page quality">
      <h1>Quality Control</h1>
      <p className="muted">
        Right-click anywhere in Scout to raise a defect, ask a question or give feedback about that spot. Tickets land here; the
        engineer works them live.
      </p>
      <div ref={host} className="quality-host" />
      {state === 'loading' && <p className="muted">Loading the board…</p>}
      {state === 'absent' && (
        <p className="muted">Quality Control is not available right now: the ticket widget did not load. Everything else in Scout works as usual.</p>
      )}
    </section>
  )
}
